import { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Info } from 'lucide-react';
import styled from 'styled-components';

import { colors } from 'constants/colors';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import {
    GetMicrostoreProductsQuery,
    GetSiteSetupDeliveryQuery,
    GetSiteSetupQuery,
    MicrostoreColumnSetupFragmentFragment,
    MicrostoreColumnWithShelvesFragmentFragment,
    SetupInstructionType,
} from 'data/__generated__';

import { SETUP_INSTRUCTION_TYPE_LABELS } from 'pages/SiteSetups/constants/types';
import { LocationDetailsSection } from '../inventory/LocationDetailsSection';

export type DeliveryProductSummary = NonNullable<
    GetSiteSetupDeliveryQuery['getSiteSetup']['delivery']
>['stockTransfersProductSummary'];

const QUANTITY_COLUMNS = [
    { label: 'Demandé', help: "Quantité demandée pour l'emplacement au réassort, avant les ruptures." },
    { label: 'Préparé', help: 'Quantité mise en caisse par le préparateur, pour toute la livraison.' },
    {
        label: 'Soldout online',
        help: "Demandé mais retiré du transfert avant la préparation, faute de stock à l'entrepôt.",
    },
    {
        label: 'Soldout bundle',
        help: 'Prévu dans la livraison mais pas trouvé au picking.',
    },
];

// "Mettre les nouveaux produits" — same layout as the inventory section: column plan on the left,
// products list on the right. Driven by the setup snapshot (MicrostoreSetupLocation):
// target product (replacementProductId), quantity to add (stockToAdd) and DLC (expiryDates).
// Next to it, the quantity requested per spiral (stockToAdd, summed into the stock transfer) and what the
// delivery actually brought per product (préparé / soldouts):
// the deliverer is told to add a product he may not have when it was soldout.
// Spirals whose product after the setup is out of stock are highlighted.
export const StoreProductsSection = ({
    column,
    columnSetup,
    deliveryProductSummary,
    outOfStockLocationIds,
    products,
    site,
    siteSetupDate,
}: {
    column: MicrostoreColumnWithShelvesFragmentFragment;
    columnSetup: MicrostoreColumnSetupFragmentFragment;
    deliveryProductSummary: DeliveryProductSummary;
    outOfStockLocationIds: Set<string>;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    site: GetSiteSetupQuery['getSiteSetup']['site'];
    siteSetupDate: string;
}) => {
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
    const [isNotRequestedProductsOpen, setIsNotRequestedProductsOpen] = useState(false);

    const setupByLocationId = new Map(
        (columnSetup.microstoreLocations ?? []).map((setupLocation) => [
            setupLocation.microstoreLocationId,
            setupLocation,
        ]),
    );
    const productsById = new Map(products.map((product) => [product._id, product]));
    const summaryByProductId = new Map(deliveryProductSummary.map((summary) => [summary.productId, summary]));

    const quantityLeftByProductId = new Map(
        deliveryProductSummary.map(({ productId, quantityPicked, quantityPositioned, quantitySoldoutBundle }) => [
            productId,
            quantitySoldoutBundle == null ? quantityPositioned : quantityPicked,
        ]),
    );
    const quantityToAddByLocationId = new Map(
        column.shelves
            .flatMap(({ locationTable }) => locationTable.flat())
            .flatMap(({ _id }): [string, number][] => {
                const setupLocation = setupByLocationId.get(_id);
                if (!setupLocation?.replacementProductId) return [];
                if (!deliveryProductSummary.length) return [[_id, setupLocation.stockToAdd]];

                const quantityLeft = quantityLeftByProductId.get(setupLocation.replacementProductId) ?? 0;
                const quantityToAdd = Math.min(setupLocation.stockToAdd, quantityLeft);
                quantityLeftByProductId.set(setupLocation.replacementProductId, quantityLeft - quantityToAdd);
                return [[_id, quantityToAdd]];
            }),
    );

    const selectedTargetProductId = selectedLocationId
        ? (setupByLocationId.get(selectedLocationId)?.replacementProductId ?? null)
        : null;

    const setupLocationsToStore = (columnSetup.microstoreLocations ?? []).filter(
        ({ replacementProductId }) => !!replacementProductId,
    );
    const targetProductIds = [
        ...new Set(setupLocationsToStore.map(({ replacementProductId }) => replacementProductId as string)),
    ];
    const targetProducts = targetProductIds
        .map((productId) => productsById.get(productId) ?? { _id: productId, name: productId, isDoubleCoil: false })
        .sort((productA, productB) => productA.name.localeCompare(productB.name));

    // Products with nothing requested (stockToAdd 0 on all their spirals) go last, folded by default. A soldout
    // product was requested: it stays in the first part, so does a requested product missing from the delivery.
    const isNotRequested = (productId: string): boolean =>
        setupLocationsToStore
            .filter(({ replacementProductId }) => replacementProductId === productId)
            .every(({ stockToAdd }) => !stockToAdd);
    const requestedProducts = targetProducts.filter(({ _id }) => !isNotRequested(_id));
    const notRequestedProducts = targetProducts.filter(({ _id }) => isNotRequested(_id));

    const renderProductRow = (targetProduct: (typeof targetProducts)[number]): JSX.Element => {
        const productSetupLocations = setupLocationsToStore.filter(
            ({ replacementProductId }) => replacementProductId === targetProduct._id,
        );
        const isSelected = productSetupLocations.some(
            ({ microstoreLocationId }) => microstoreLocationId === selectedLocationId,
        );
        const summary = summaryByProductId.get(targetProduct._id);
        return (
            <tr key={targetProduct._id}>
                <ProductName
                    onClick={() => setSelectedLocationId(productSetupLocations[0]?.microstoreLocationId ?? null)}
                    $isSelected={isSelected}
                >
                    {targetProduct.name}
                </ProductName>
                <td>
                    {productSetupLocations.map((setupLocation, index) => (
                        <LocationName key={setupLocation.microstoreLocationId}>Emplacement {index + 1}</LocationName>
                    ))}
                </td>
                <td>
                    {productSetupLocations.map((setupLocation) => (
                        <div key={setupLocation.microstoreLocationId}>{setupLocation.stockToAdd}</div>
                    ))}
                </td>
                {/* Empty cells rather than dashes or zeros: only what matters stands out. */}
                <td>{summary?.quantityPicked}</td>
                <td>
                    {summary?.quantitySoldoutOnline ? <SoldoutText>{summary.quantitySoldoutOnline}</SoldoutText> : null}
                </td>
                <td>
                    {/* null until the preparation is complete */}
                    {summary?.quantitySoldoutBundle ? <SoldoutText>{summary.quantitySoldoutBundle}</SoldoutText> : null}
                </td>
            </tr>
        );
    };

    return (
        <Container>
            <Title>{SETUP_INSTRUCTION_TYPE_LABELS[SetupInstructionType.StoreProducts]}</Title>
            <Content>
                <ColumnDisplayContainer>
                    <PlanTitle>Après rangement</PlanTitle>
                    <Column>
                        {column.shelves.map((shelf) => (
                            <Shelf key={shelf._id} $numberOfLocationRow={shelf.locationTable.length}>
                                {shelf.locationTable.map((locationRow, rowIndex) => (
                                    <LocationRow key={rowIndex} $isFirstRow={rowIndex === 0}>
                                        {locationRow.map((location) => {
                                            const setupLocation = setupByLocationId.get(location._id);
                                            const targetProductId = setupLocation?.replacementProductId ?? null;
                                            const hasTarget = !!targetProductId;
                                            const isSelected =
                                                selectedLocationId === location._id ||
                                                (!!selectedTargetProductId &&
                                                    targetProductId === selectedTargetProductId);
                                            return (
                                                <Location
                                                    key={location._id}
                                                    onClick={() => setSelectedLocationId(location._id)}
                                                    $isSelected={isSelected}
                                                    $hasTarget={hasTarget}
                                                    $isOutOfStock={outOfStockLocationIds.has(location._id)}
                                                    $isDoubleCoil={
                                                        !!productsById.get(targetProductId ?? location.productId ?? '')
                                                            ?.isDoubleCoil
                                                    }
                                                >
                                                    <LocationText>
                                                        {hasTarget ? quantityToAddByLocationId.get(location._id) : ''}
                                                    </LocationText>
                                                </Location>
                                            );
                                        })}
                                    </LocationRow>
                                ))}
                            </Shelf>
                        ))}
                    </Column>
                    <LegendsContainer>
                        <Legend>
                            <LegendSymbol>
                                <Location $isSelected={false} $hasTarget>
                                    <LocationText>5</LocationText>
                                </Location>
                            </LegendSymbol>
                            <LegendText>Emplacement à remplir (quantité à ajouter)</LegendText>
                        </Legend>
                        <Legend>
                            <LegendSymbol>
                                <Location $isSelected $hasTarget>
                                    <LocationText>5</LocationText>
                                </Location>
                            </LegendSymbol>
                            <LegendText>Emplacement sélectionné</LegendText>
                        </Legend>
                        {outOfStockLocationIds.size ? (
                            <Legend>
                                <LegendSymbol>
                                    <Location $isSelected={false} $isOutOfStock />
                                </LegendSymbol>
                                <LegendText>Produit après rangement en stock à 0</LegendText>
                            </Legend>
                        ) : null}
                    </LegendsContainer>
                    <LocationDetailsSection
                        columnSetup={columnSetup}
                        isAfterSetup
                        selectedLocationId={selectedLocationId}
                        products={products}
                        site={site}
                        siteSetupDate={siteSetupDate}
                    />
                </ColumnDisplayContainer>

                <ProductsContainer>
                    {targetProducts.length === 0 ? (
                        <EmptyState>Aucun produit à ajouter.</EmptyState>
                    ) : (
                        <ProductsTable>
                            <thead>
                                <tr>
                                    <th>Produit</th>
                                    <th>Emplacement</th>
                                    {QUANTITY_COLUMNS.map(({ label, help }) => (
                                        <th key={label}>
                                            {label}
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <HelpIcon aria-label={`Aide — ${label}`}>
                                                        <Info size={13} />
                                                    </HelpIcon>
                                                </TooltipTrigger>
                                                <TooltipContent>{help}</TooltipContent>
                                            </Tooltip>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {requestedProducts.map(renderProductRow)}
                                {notRequestedProducts.length ? (
                                    <>
                                        <tr>
                                            <PartTitle
                                                colSpan={6}
                                                onClick={() =>
                                                    setIsNotRequestedProductsOpen(!isNotRequestedProductsOpen)
                                                }
                                            >
                                                Produits non demandés ({notRequestedProducts.length})
                                                {isNotRequestedProductsOpen ? <FaChevronUp /> : <FaChevronDown />}
                                            </PartTitle>
                                        </tr>
                                        {isNotRequestedProductsOpen ? notRequestedProducts.map(renderProductRow) : null}
                                    </>
                                ) : null}
                            </tbody>
                        </ProductsTable>
                    )}
                </ProductsContainer>
            </Content>
        </Container>
    );
};

const Container = styled.div``;

const Title = styled.div`
    font-size: 18px;
    font-weight: bold;
    text-align: center;
`;

const Content = styled.div`
    display: flex;
    margin-top: 20px;
`;

const ColumnDisplayContainer = styled.div`
    width: 30%;
`;

const PlanTitle = styled.div`
    font-weight: bold;
    text-align: center;
    margin-bottom: 5px;
`;

const Column = styled.div`
    border-radius: 5px;
    padding: 5px;
    background-color: ${colors.pureWhite};
    width: 100%;
`;

const Shelf = styled.div<{ $numberOfLocationRow: number }>`
    width: 90%;
    margin: auto;
    transform: perspective(1200px) rotateX(45deg);
    /* Because of perspective, we need to readjust the margins */
    margin-top: ${({ $numberOfLocationRow }) => 15 - $numberOfLocationRow * 7}px;
    margin-bottom: ${({ $numberOfLocationRow }) => -$numberOfLocationRow * 5}px;
`;

const LocationRow = styled.div<{ $isFirstRow: boolean }>`
    display: flex;
    flex-direction: row;
    border: 1px solid ${colors.darkGrey};
    border-top: ${({ $isFirstRow }) => ($isFirstRow ? 1 : 0)}px solid ${colors.darkGrey};
    background-color: ${colors.pureWhite};
`;

const Location = styled.div<{
    $isSelected: boolean;
    $hasTarget?: boolean;
    $isOutOfStock?: boolean;
    $isDoubleCoil?: boolean;
}>`
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    text-align: center;
    /* A double coil spans two single spirals (same rule as TomorrowShelf). */
    flex: ${({ $isDoubleCoil }) => ($isDoubleCoil ? '2 0 0' : '1 0 0')};
    background-color: ${({ $isOutOfStock }) => ($isOutOfStock ? colors.red : 'transparent')};
    border: ${({ $isSelected, $hasTarget }) => {
        if ($isSelected) return `3px solid ${colors.green}`;
        if ($hasTarget) return `2px solid ${colors.violet}`;
        return `1px solid ${colors.darkGrey}`;
    }};
    padding: 1px;
    margin: 2px;
    height: 35px;
`;

const LocationText = styled.span<{ $color?: string }>`
    font-size: 14px;
    color: ${({ $color }) => $color || colors.pureBlack};
`;

const LegendsContainer = styled.div`
    margin-top: 20px;
    padding: 10px;
`;
const Legend = styled.div`
    display: flex;
    align-items: center;
`;
const LegendSymbol = styled.div`
    width: 60px;
`;
const LegendText = styled.div`
    margin-left: 10px;
`;

const ProductsContainer = styled.div`
    flex-grow: 1;
    margin-left: 10px;
`;
const ProductsTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    & th {
        text-align: left;
        white-space: nowrap;
        border-bottom: 1px solid ${colors.borderGrey};
    }
    & td {
        vertical-align: top;
        padding: 3px 5px;
        border-top: 1px solid ${colors.lightGrey};
    }
`;
const ProductName = styled.td<{ $isSelected: boolean }>`
    cursor: pointer;
    font-weight: bold;
    color: ${({ $isSelected }) => ($isSelected ? colors.greenDarker : colors.pureBlack)};
`;
const HelpIcon = styled.span`
    display: inline-flex;
    vertical-align: middle;
    margin-left: 4px;
    cursor: help;
    color: ${colors.darkGrey};
`;
const PartTitle = styled.td`
    cursor: pointer;
    & svg {
        display: inline-block;
        vertical-align: middle;
        margin-left: 5px;
    }
    padding-top: 15px !important;
    font-weight: bold;
    color: ${colors.red};
`;
const LocationName = styled.div`
    font-style: italic;
`;
const SoldoutText = styled.span`
    color: ${colors.red};
    font-weight: 800;
`;
const EmptyState = styled.div`
    text-align: center;
    color: ${colors.darkGrey};
`;
